import { classToPlain, Exclude, Expose, plainToClass, Type } from "class-transformer";
import * as t from "io-ts";
import { v4 as uuid } from "uuid";
import { validateJSON, validateJSONNow } from "../web/validation";
import { CompanyEntity, CompanyValidator } from "./company";
import { Entity } from "./entity";
import { SiteEntity, SiteValidator } from "./site";

export const SiteAssignmentCompanyAssocValidator = t.intersection([
  t.type({
    id: t.string,
    siteAssignmentId: t.string,
    companyId: t.string,
    normalPreferenceRank: t.number,
    servicePreferenceRank: t.number,
  }),
  t.partial({
    company: CompanyValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const SiteAssignmentCompanyAssocArrayValidator = t.array(
  SiteAssignmentCompanyAssocValidator
);

export type SiteAssignmentCompanyAssoc = t.TypeOf<
  typeof SiteAssignmentCompanyAssocValidator
>;
export type SiteAssignmentCompanyAssocArray = t.TypeOf<
  typeof SiteAssignmentCompanyAssocArrayValidator
>;

/**
 * A site assignment.
 */
@Exclude()
export class SiteAssignmentCompanyAssocEntity implements Entity {
  @Expose() id!: string;
  @Expose() siteAssignmentId!: string;
  @Expose() companyId!: string;
  @Expose() normalPreferenceRank!: number;
  @Expose() servicePreferenceRank!: number;
  @Expose() private createDttm?: number;
  @Expose() private lastModifiedDttm?: number;

  @Type(() => CompanyEntity)
  @Expose()
  company?: CompanyEntity;

  readonly entityType = 'SITEASSIGNCOMPASSOC';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for SiteAssignmentCompanyAssocEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a SiteAssignmentCompanyAssocEntity.
   *
   * @param siteAssignmentId the primary key of the site assignment
   * @p
   * @param id (optional) The primary key of the well.
   */
  static create(
    siteAssignmentId: string,
    companyId: string,
    normalPreferenceRank: number,
    servicePreferenceRank: number,
    id: string | undefined = uuid()
  ): SiteAssignmentCompanyAssocEntity {
    const entity = new SiteAssignmentCompanyAssocEntity();
    entity.id = id;
    entity.siteAssignmentId = siteAssignmentId;
    entity.companyId = companyId;
    entity.normalPreferenceRank = normalPreferenceRank;
    entity.servicePreferenceRank = servicePreferenceRank;
    entity.createDate = new Date();
    entity.lastModifiedDate = entity.createDate;
    return entity;
  }

  /**
   * Create a SiteAssignmentCompanyAssocEntity from a type value.
   *
   * @param siteAssignmentCompanyAssoc the type value.
   */
  static from(
    siteAssignmentCompanyAssoc: SiteAssignmentCompanyAssoc
  ): SiteAssignmentCompanyAssocEntity {
    const entity: SiteAssignmentCompanyAssocEntity = plainToClass(
      SiteAssignmentCompanyAssocEntity,
      siteAssignmentCompanyAssoc,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the SiteAssignmentCompanyAssocEntity to a SiteAssignmentCompanyAssoc type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<SiteAssignmentCompanyAssoc> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SiteAssignmentCompanyAssocValidator, object);
  }

  /**
   * Deserializes the SiteAssignmentCompanyAssocEntity to a SiteAssignmentCompanyAssoc type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValueNow(): SiteAssignmentCompanyAssoc {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(SiteAssignmentCompanyAssocValidator, object);
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }

  get lastModifiedDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.lastModifiedDttm) {
      return new Date(this.lastModifiedDttm);
    } else {
      return undefined;
    }
  }

  set lastModifiedDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.lastModifiedDttm = date.getTime();
    } else {
      this.lastModifiedDttm = undefined;
    }
  }
}

export const SiteAssignmentSiteAssocValidator = t.intersection([
  t.type({
    id: t.string,
    siteAssignmentId: t.string,
    siteId: t.string,
    normalPreferenceRank: t.number,
    servicePreferenceRank: t.number,
  }),
  t.partial({
    site: SiteValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const SiteAssignmentSiteAssocArrayValidator = t.array(
  SiteAssignmentSiteAssocValidator
);

export type SiteAssignmentSiteAssoc = t.TypeOf<
  typeof SiteAssignmentSiteAssocValidator
>;
export type SiteAssignmentSiteAssocArray = t.TypeOf<
  typeof SiteAssignmentSiteAssocArrayValidator
>;

/**
 * A site assignment.
 */
@Exclude()
export class SiteAssignmentSiteAssocEntity implements Entity {
  @Expose() id!: string;
  @Expose() siteAssignmentId!: string;
  @Expose() siteId!: string;
  @Expose() normalPreferenceRank!: number;
  @Expose() servicePreferenceRank!: number;
  @Expose() private createDttm?: number;
  @Expose() private lastModifiedDttm?: number;

  @Type(() => SiteEntity)
  @Expose()
  site?: SiteEntity;

  readonly entityType = 'SITEASSIGNSITEASSOC';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for SiteAssignmentSiteAssocEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a SiteAssignmentSiteAssocEntity.
   *
   * @param siteAssignmentId the primary key of the site assignment
   * @p
   * @param id (optional) The primary key of the well.
   */
  static create(
    siteAssignmentId: string,
    siteId: string,
    normalPreferenceRank: number,
    servicePreferenceRank: number,
    id: string | undefined = uuid()
  ): SiteAssignmentSiteAssocEntity {
    const entity = new SiteAssignmentSiteAssocEntity();
    entity.id = id;
    entity.siteAssignmentId = siteAssignmentId;
    entity.siteId = siteId;
    entity.normalPreferenceRank = normalPreferenceRank;
    entity.servicePreferenceRank = servicePreferenceRank;
    entity.createDate = new Date();
    entity.lastModifiedDate = entity.createDate;
    return entity;
  }

  /**
   * Create a SiteAssignmentSiteAssocEntity from a type value.
   *
   * @param siteAssignmentSiteAssoc the type value.
   */
  static from(
    siteAssignmentSiteAssoc: SiteAssignmentSiteAssoc
  ): SiteAssignmentSiteAssocEntity {
    const entity: SiteAssignmentSiteAssocEntity = plainToClass(
      SiteAssignmentSiteAssocEntity,
      siteAssignmentSiteAssoc,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the SiteAssignmentSiteAssocEntity to a SiteAssignmentSiteAssoc type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<SiteAssignmentSiteAssoc> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SiteAssignmentSiteAssocValidator, object);
  }

  /**
   * Deserializes the SiteAssignmentSiteAssocEntity to a SiteAssignmentSiteAssoc type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValueNow(): SiteAssignmentSiteAssoc {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(SiteAssignmentSiteAssocValidator, object);
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }

  get lastModifiedDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.lastModifiedDttm) {
      return new Date(this.lastModifiedDttm);
    } else {
      return undefined;
    }
  }

  set lastModifiedDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.lastModifiedDttm = date.getTime();
    } else {
      this.lastModifiedDttm = undefined;
    }
  }
}
